import type { GridCsvExportOptions } from "@mui/x-data-grid-premium";
import { gridFilteredSortedRowIdsSelector } from "@mui/x-data-grid-premium";
import type { Moment } from "moment";
import moment from "moment";
import { getLocalDateTime, setDateTimeFormate } from "src/common/utils/app.util";

interface GridColumn {
  field: string;
  headerName?: string;
  type?: string;
  valueFormatter?: unknown;
  [key: string]: unknown;
}

/** US display strings written by grid formatters / page transforms (not raw API ISO). */
const US_DISPLAY_DATE_ONLY = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
const US_DISPLAY_DATETIME = /^\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}(?::\d{2})?\s*(AM|PM)$/i;

interface ExcelCellLike {
  value: unknown;
  numFmt?: string;
}

/**
 * Excel display conventions (US-style), aligned with grid formatters.
 * Date/datetime cells are exported as **native Excel date serials** (not text) with
 * {@link EXCEL_DATE_NUM_FMT} / {@link EXCEL_DATETIME_NUM_FMT} so Excel date filters work,
 * the formula bar respects minute precision, and US formats replace MUI's `dd.mm.yyyy`.
 */
export const EXCEL_DATE_NUM_FMT = "mm/dd/yyyy";
export const EXCEL_DATETIME_NUM_FMT = "mm/dd/yyyy hh:mm AM/PM";
const EXCEL_EXPORT_DATE_DISPLAY_FMT = "MM/DD/YYYY";
const EXCEL_EXPORT_DATETIME_DISPLAY_FMT = "MM/DD/YYYY hh:mm A";

const DATE_INPUT_FORMATS: ReadonlyArray<string | typeof moment.ISO_8601> = [
  "MM/DD/YYYY",
  "DD.MM.YYYY",
  "YYYY-MM-DD",
  moment.ISO_8601,
];

const DATETIME_INPUT_FORMATS: ReadonlyArray<string | typeof moment.ISO_8601> = [
  "MM/DD/YYYY hh:mm:ss A",
  "MM/DD/YYYY HH:mm:ss",
  "MM/DD/YYYY hh:mm A",
  "DD.MM.YYYY HH:mm",
  "D.M.YYYY H:mm",
  "DD.MM.YYYY",
  "YYYY-MM-DDTHH:mm:ss",
  moment.ISO_8601,
];

function parseExportDateValue(value: unknown): Moment {
  if (value instanceof Date) {
    return moment(value);
  }
  if (typeof value === "string") {
    return moment.utc(value, [...DATE_INPUT_FORMATS]);
  }
  return moment(value as string | number | Date);
}

function parseExportDateTimeValue(value: unknown): Moment {
  if (value instanceof Date) {
    return moment(value);
  }
  if (typeof value === "string") {
    return moment.utc(value, [...DATETIME_INPUT_FORMATS]);
  }
  return moment(value as string | number | Date);
}

type ExcelExportDateInput = string | number | Date;

interface ExcelDateFormatContext {
  rawRowValue?: unknown;
  row?: Record<string, unknown>;
  colDef: GridColumn;
}

function isUnsupportedCellValue(value: unknown): boolean {
  return typeof value === "object" && value !== null && !(value instanceof Date);
}

function isUsDisplayDateString(value: unknown, isDateTime: boolean): boolean {
  if (typeof value !== "string") {
    return false;
  }
  const trimmed = value.trim();
  return isDateTime ? US_DISPLAY_DATETIME.test(trimmed) : US_DISPLAY_DATE_ONLY.test(trimmed);
}

function toLocalDateTimeInput(value: unknown): string | Date | number | undefined {
  if (value instanceof Date || typeof value === "string" || typeof value === "number") {
    return value;
  }
  return undefined;
}

/** True when the value is a UTC timestamp that needs local conversion (not date-only ISO). */
function isBackendDateTimeValue(value: unknown): boolean {
  if (value instanceof Date || typeof value === "number") {
    return true;
  }
  if (typeof value !== "string") {
    return false;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  return trimmed.includes("T") || trimmed.includes("Z") || trimmed.includes("UTC");
}

interface GridValueFormatterParams {
  value: unknown;
  row?: Record<string, unknown>;
  field?: string;
}

/** Coerces formatter/export values to display text without `[object Object]`. */
function coerceExportDisplayString(value: unknown): string | null {
  if (value == null || value === "") {
    return null;
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return null;
}

/** Parses US display strings; accepts optional `:ss` on input only, never on output. */
const US_DISPLAY_NORMALIZE_PARSE_DATETIME: ReadonlyArray<string> = [
  "MM/DD/YYYY hh:mm A",
  "MM/DD/YYYY hh:mm:ss A",
];

function normalizeUsDisplayString(value: string, isDateTime: boolean): string {
  const displayFmt = isDateTime ? EXCEL_EXPORT_DATETIME_DISPLAY_FMT : EXCEL_EXPORT_DATE_DISPLAY_FMT;
  const parsed = moment(
    value.trim(),
    isDateTime ? [...US_DISPLAY_NORMALIZE_PARSE_DATETIME] : ["MM/DD/YYYY"],
    true
  );
  if (parsed.isValid()) {
    return parsed.seconds(0).milliseconds(0).format(displayFmt);
  }
  return value.trim();
}

function ensureMinutePrecisionDisplay(
  text: string | null,
  isDateTime: boolean
): string | null {
  if (text == null || text === "" || !isDateTime) {
    return text;
  }
  return normalizeUsDisplayString(text, true);
}

function invokeColumnValueFormatter(
  formatter: unknown,
  value: unknown,
  row: Record<string, unknown> | undefined,
  colDef: GridColumn
): string | null {
  if (typeof formatter !== "function") {
    return null;
  }
  const attempts: Array<() => unknown> = [
    () => (formatter as (cellValue: unknown) => unknown)(value),
    () =>
      (formatter as (params: GridValueFormatterParams) => unknown)({
        value,
        row,
        field: colDef.field,
      }),
    () =>
      (formatter as (cellValue: unknown, rowModel: unknown, column: unknown) => unknown)(
        value,
        row,
        colDef
      ),
  ];
  for (const attempt of attempts) {
    try {
      const display = coerceExportDisplayString(attempt());
      if (display) {
        return display;
      }
    } catch {
      // try next signature
    }
  }
  return null;
}

function tryFormatFromValueFormatter(
  context: ExcelDateFormatContext | undefined,
  source: unknown,
  isDateTime: boolean
): string | null {
  if (context?.colDef.valueFormatter == null) {
    return null;
  }
  const formatterOutput = invokeColumnValueFormatter(
    context.colDef.valueFormatter,
    source,
    context.row,
    context.colDef
  );
  if (!formatterOutput) {
    return null;
  }
  return ensureMinutePrecisionDisplay(formatterOutput, isDateTime);
}

/**
 * Formats a date/datetime for Excel export to match what the grid shows:
 * 1. Column valueFormatter (if present)
 * 2. Already-formatted US display strings (pass-through / normalize, no UTC shift)
 * 3. Raw backend values (UTC → local via getLocalDateTime)
 * 4. Fallback setDateTimeFormate (grids without conversion)
 */
export function formatExcelDateValueForExport(
  value: ExcelExportDateInput,
  isDateTime: boolean,
  context?: ExcelDateFormatContext
): string | null {
  const displayFmt = isDateTime ? EXCEL_EXPORT_DATETIME_DISPLAY_FMT : EXCEL_EXPORT_DATE_DISPLAY_FMT;
  const source = context?.rawRowValue ?? value;

  const fromFormatter = tryFormatFromValueFormatter(context, source, isDateTime);
  if (fromFormatter) {
    return fromFormatter;
  }

  if (typeof source === "string" && isUsDisplayDateString(source, isDateTime)) {
    return normalizeUsDisplayString(source, isDateTime);
  }

  if (isBackendDateTimeValue(source)) {
    const dateInput = toLocalDateTimeInput(source);
    if (dateInput !== undefined) {
      const local = getLocalDateTime(dateInput, displayFmt);
      return ensureMinutePrecisionDisplay(local ?? "", isDateTime);
    }
  }

  const dateInput = toLocalDateTimeInput(source) ?? value;
  return ensureMinutePrecisionDisplay(setDateTimeFormate(dateInput, displayFmt), isDateTime);
}

function buildUtcDateFromComponents(
  year: number,
  month: number,
  day: number,
  hours = 0,
  minutes = 0
): Date {
  return new Date(Date.UTC(year, month, day, hours, minutes, 0));
}

const EXCEL_SERIAL_EPOCH_MS = Date.UTC(1899, 11, 30);
const MS_PER_DAY = 86_400_000;
const MINUTES_PER_DAY = 1_440;

/**
 * Excel date serial from calendar fields (minute precision, zero seconds).
 * Uses integer day count + minute fraction to avoid float drift in stored values.
 */
export function toExcelSerialFromLocalComponents(
  year: number,
  month: number,
  day: number,
  hours: number,
  minutes: number
): number {
  const wholeDays = Math.round((Date.UTC(year, month, day) - EXCEL_SERIAL_EPOCH_MS) / MS_PER_DAY);
  return wholeDays + (hours * 60 + minutes) / MINUTES_PER_DAY;
}

/** Excel serial for date-only columns (no time fraction — avoids 12:00 PM in the formula bar). */
export function toExcelSerialDateOnly(year: number, month: number, day: number): number {
  return toExcelSerialFromLocalComponents(year, month, day, 0, 0);
}

/** @deprecated Prefer {@link toExcelSerialFromLocalComponents} for new code paths. */
export function toExcelSerialRoundedToMinute(date: Date): number {
  return toExcelSerialFromLocalComponents(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes()
  );
}

const US_DISPLAY_PARSE_FORMATS_DATE: ReadonlyArray<string> = ["MM/DD/YYYY"];
/** Minute-precision display only (no `:ss`); seconds are never written to Excel. */
const US_DISPLAY_PARSE_FORMATS_DATETIME: ReadonlyArray<string> = ["MM/DD/YYYY hh:mm A"];

export function displayStringToExcelNativeDate(
  displayValue: string,
  isDateTime: boolean
): Date | null {
  const parsed = moment(
    displayValue.trim(),
    isDateTime ? [...US_DISPLAY_PARSE_FORMATS_DATETIME] : [...US_DISPLAY_PARSE_FORMATS_DATE],
    true
  );
  if (!parsed.isValid()) {
    return null;
  }
  const minuteAligned = parsed.seconds(0).milliseconds(0);
  if (isDateTime) {
    return buildUtcDateFromComponents(
      minuteAligned.year(),
      minuteAligned.month(),
      minuteAligned.date(),
      minuteAligned.hour(),
      minuteAligned.minute()
    );
  }
  return buildUtcDateFromComponents(
    minuteAligned.year(),
    minuteAligned.month(),
    minuteAligned.date(),
    0,
    0
  );
}

function assignParsedDateToCell(
  cell: ExcelCellLike,
  displayText: string | null,
  isDateTime: boolean
): void {
  if (displayText == null || displayText === "") {
    return;
  }
  const excelDate = displayStringToExcelNativeDate(displayText, isDateTime);
  if (!excelDate) {
    return;
  }
  if (isDateTime) {
    cell.value = toExcelSerialFromLocalComponents(
      excelDate.getUTCFullYear(),
      excelDate.getUTCMonth(),
      excelDate.getUTCDate(),
      excelDate.getUTCHours(),
      excelDate.getUTCMinutes()
    );
    cell.numFmt = EXCEL_DATETIME_NUM_FMT;
    return;
  }
  cell.value = toExcelSerialDateOnly(
    excelDate.getUTCFullYear(),
    excelDate.getUTCMonth(),
    excelDate.getUTCDate()
  );
  cell.numFmt = EXCEL_DATE_NUM_FMT;
}

function applyDateOrDateTimeFormatting(
  cell: ExcelCellLike,
  colDef: GridColumn,
  context?: Omit<ExcelDateFormatContext, "colDef">
): void {
  if (cell.value == null || cell.value === "") {
    return;
  }
  if (isUnsupportedCellValue(cell.value)) {
    return;
  }
  const isDateTime = colDef.type === "dateTime";
  const isDate = colDef.type === "date";
  if (!isDate && !isDateTime) {
    return;
  }

  const exportValue = cell.value as ExcelExportDateInput;
  const parsed = isDateTime ? parseExportDateTimeValue(exportValue) : parseExportDateValue(exportValue);
  if (!parsed.isValid()) {
    return;
  }

  const formatContext: ExcelDateFormatContext = { ...context, colDef };
  const displayText = formatExcelDateValueForExport(exportValue, isDateTime, formatContext);
  assignParsedDateToCell(cell, displayText, isDateTime);
}

function resolveRowModel(apiRef: any, rowId: unknown): Record<string, unknown> | undefined {
  const row = apiRef?.current?.getRow?.(rowId);
  if (row && typeof row === "object") {
    return row as Record<string, unknown>;
  }
  const rowModels = apiRef?.current?.getRowModels?.();
  if (rowModels instanceof Map) {
    const model = rowModels.get(rowId);
    if (model && typeof model === "object") {
      return model as Record<string, unknown>;
    }
  }
  return undefined;
}

function resolveRowsToExport(apiRef: any): unknown[] {
  if (!apiRef?.current) {
    return [];
  }
  const eligibleRowIds = resolveEligibleRowIds(apiRef);
  const selectedRowIds = resolveSelectedRowIds(apiRef);

  if (selectedRowIds.length > 0) {
    const eligibleSet = new Set(eligibleRowIds);
    return selectedRowIds.filter((id) => eligibleSet.has(id));
  }
  if (eligibleRowIds.length > 0) {
    return eligibleRowIds;
  }
  return resolveVisibleRowIdsFallback(apiRef);
}

/**
 * Merges toolbar CSV options with `escapeFormulas` set to `false`.
 *
 * MUI X (v6+) defaults `escapeFormulas` to `true`, which wraps cell strings that start with
 * `-`, `+`, `=`, `@`, etc. in a form Excel reads as text with a leading apostrophe
 * (e.g. negative currency / percentages). MUI X v5 ignores this option, so the override
 * is a no-op there but keeps Jan-IT and the Vendor Portal in sync.
 *
 * Cast through `unknown` because v5 typings of `GridCsvExportOptions` do not yet include
 * `escapeFormulas`. The runtime grid simply ignores unknown keys.
 */
export function withGridCsvExportDefaults(options: GridCsvExportOptions): GridCsvExportOptions {
  return {
    ...options,
    escapeFormulas: false,
  } as unknown as GridCsvExportOptions;
}

function resolveEligibleRowIds(apiRef: any): unknown[] {
  try {
    const ids = gridFilteredSortedRowIdsSelector(apiRef as any);
    if (Array.isArray(ids)) return ids;
  } catch {
    // ignore selector errors and fall back to the API methods below
  }
  const sortedRowIds = apiRef.current.getSortedRowIds?.();
  if (Array.isArray(sortedRowIds) && sortedRowIds.length > 0) return sortedRowIds;
  const visibleRowModels = apiRef.current.getVisibleRowModels?.();
  if (visibleRowModels instanceof Map) {
    return Array.from(visibleRowModels.keys());
  }
  return [];
}

function resolveVisibleRowIdsFallback(apiRef: any): unknown[] {
  const visibleRowModels = apiRef.current.getVisibleRowModels?.();
  if (visibleRowModels instanceof Map) {
    return Array.from(visibleRowModels.keys());
  }
  return [];
}

function resolveSelectedRowIds(apiRef: any): unknown[] {
  const selectedRows = apiRef.current.getSelectedRows?.();
  if (selectedRows instanceof Map) {
    return Array.from(selectedRows.keys());
  }
  return [];
}

function filterColumnsForExport(
  columns: GridColumn[],
  columnsToExcludeFromExport?: string[]
): GridColumn[] {
  if (!columnsToExcludeFromExport?.length) {
    return columns;
  }
  return columns.filter((col) => !columnsToExcludeFromExport.includes(col.field));
}

function buildFieldIndexMapFromHeader(
  headerRow: { eachCell: (cb: (cell: { value: unknown }, colIdx: number) => void) => void },
  visibleCols: GridColumn[]
): Record<string, number> {
  const fieldIndexMap: Record<string, number> = {};
  headerRow.eachCell((cell, colIdx) => {
    const col = visibleCols.find(
      (c) => c.headerName === cell.value || c.field === cell.value
    );
    if (col) {
      fieldIndexMap[col.field] = colIdx;
    }
  });
  return fieldIndexMap;
}

function formatRowDateCells(
  row: { getCell: (colIdx: number) => ExcelCellLike | undefined },
  fieldIndexMap: Record<string, number>,
  visibleCols: GridColumn[],
  rowModel: Record<string, unknown> | undefined
): void {
  Object.entries(fieldIndexMap).forEach(([field, colIdx]) => {
    try {
      const colDef = visibleCols.find((c) => c.field === field);
      const cell = row.getCell(colIdx);
      if (!colDef || !cell) {
        return;
      }
      applyDateOrDateTimeFormatting(cell, colDef, {
        rawRowValue: rowModel?.[field],
        row: rowModel,
      });
    } catch (error) {
      console.warn(`Failed to format cell for field ${field}:`, error);
    }
  });
}

function formatWorksheetDateColumns(
  worksheet: {
    getRow: (index: number) => { eachCell: (cb: (cell: { value: unknown }, colIdx: number) => void) => void };
    eachRow: (cb: (row: { getCell: (colIdx: number) => ExcelCellLike | undefined }, rowIndex: number) => void) => void;
  },
  apiRef: any,
  fieldIndexMap: Record<string, number>,
  visibleCols: GridColumn[]
): void {
  const exportedRowIds = resolveRowsToExport(apiRef);
  worksheet.eachRow((row, rowIndex) => {
    if (rowIndex === 1) {
      return;
    }
    const rowId = exportedRowIds[rowIndex - 2];
    const rowModel = rowId == null ? undefined : resolveRowModel(apiRef, rowId);
    formatRowDateCells(row, fieldIndexMap, visibleCols, rowModel);
  });
}

/**
 * Creates Excel export options for the DataGrid.
 *
 * - Formats date/dateTime columns as display strings (same as grid formatters)
 *   with US `numFmt` so Excel's formula bar has no `:ss` and Format Cells matches
 *   Jan-IT (`mm/dd/yyyy` / `mm/dd/yyyy hh:mm AM/PM`).
 * - Honors filters, sorts, selection, and column-exclude lists across all
 *   grids that share `CommonXGrid`.
 *
 * @param apiRef Grid API reference to access data
 * @param columnsToExcludeFromExport Optional list of column field names to
 *   exclude from export (e.g. `["action", "actions", "jsonData"]`)
 */
export const getExcelOptions = (apiRef: any, columnsToExcludeFromExport?: string[]) => {
  return {
    getRowsToExport: () => {
      try {
        return resolveRowsToExport(apiRef);
      } catch (error) {
        console.error("Error in getRowsToExport:", error);
        return [];
      }
    },

    getColumnsToExport: () => {
      try {
        const visible = apiRef.current.getVisibleColumns?.() ?? [];
        return filterColumnsForExport(visible, columnsToExcludeFromExport);
      } catch (error) {
        console.error("Error in getColumnsToExport:", error);
        return [];
      }
    },

    exceljsPostProcess: async ({ worksheet }: any): Promise<void> => {
      try {
        const visibleCols = filterColumnsForExport(
          apiRef.current.getVisibleColumns?.() ?? [],
          columnsToExcludeFromExport
        );
        const fieldIndexMap = buildFieldIndexMapFromHeader(worksheet.getRow(1), visibleCols);
        formatWorksheetDateColumns(worksheet, apiRef, fieldIndexMap, visibleCols);
      } catch (error) {
        console.error("Error in exceljsPostProcess:", error);
      }
    },
  };
};
